/**
 * tokenStore — Unified token storage with cookie-first reads.
 *
 * Migration strategy (Phase 8 Task 3 — localStorage → httpOnly cookie):
 * - Reads: `document.cookie` first (where the backend can set httpOnly
 *   cookies), then falls back to `localStorage` for backward compatibility
 *   during the migration window.
 * - Writes: dual-write to BOTH the cookie (`path=/`, `SameSite=Strict`,
 *   `Secure` on HTTPS) and `localStorage` so legacy readers and existing
 *   tests keep working.
 * - Removes: clears both stores.
 *
 * NOTE: Cookies set from JavaScript cannot be httpOnly. Full XSS elimination
 * completes when the backend sets httpOnly cookies and the localStorage
 * fallback is removed (Phase 9 cutover). This module is the single choke
 * point for that cutover.
 */

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days

// SameSite: Strict in single-origin prod, Lax in dev (localhost / 192.168.x /
// 10.x / .local). Strict on dev would drop the cookie on cross-port
// requests and surface as recurring 401.
const computeSameSite = (): string => {
  if (typeof window === 'undefined') return 'Strict';
  const host = window.location?.hostname ?? '';
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.startsWith('192.168.') ||
    host.startsWith('10.') ||
    host.endsWith('.local')
  ) {
    return 'Lax';
  }
  return 'Strict';
};

const setCookie = (name: string, value: string, maxAge?: number): void => {
  const age = maxAge ?? COOKIE_MAX_AGE;
  if (typeof document === 'undefined') return;
  const secure =
    typeof window !== 'undefined' && window.location?.protocol === 'https:'
      ? '; Secure'
      : '';
  const sameSite = computeSameSite();
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${age}; SameSite=${sameSite}${secure}`;
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
};

const deleteCookie = (name: string): void => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Strict`;
};

const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      // localStorage unavailable (SSR, private mode, storage disabled)
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // localStorage unavailable — cookie still holds the value
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // localStorage unavailable — cookie already cleared
    }
  },
};

/**
 * Read the access token: cookie first, localStorage fallback.
 */
export const getAccessToken = (): string | null => {
  return (
    getCookie(ACCESS_TOKEN_KEY) || safeLocalStorage.getItem(ACCESS_TOKEN_KEY)
  );
};

/**
 * Persist the access token to both cookie and localStorage.
 */
export const setAccessToken = (token: string): void => {
  setCookie(ACCESS_TOKEN_KEY, token);
  safeLocalStorage.setItem(ACCESS_TOKEN_KEY, token);
};

/**
 * Clear the access token from both cookie and localStorage.
 */
export const removeAccessToken = (): void => {
  deleteCookie(ACCESS_TOKEN_KEY);
  safeLocalStorage.removeItem(ACCESS_TOKEN_KEY);
};

/**
 * Read the refresh token: cookie first, localStorage fallback.
 */
export const getRefreshToken = (): string | null => {
  return (
    getCookie(REFRESH_TOKEN_KEY) || safeLocalStorage.getItem(REFRESH_TOKEN_KEY)
  );
};

/**
 * Persist the refresh token to both cookie and localStorage.
 */
export const setRefreshToken = (token: string): void => {
  setCookie(REFRESH_TOKEN_KEY, token);
  safeLocalStorage.setItem(REFRESH_TOKEN_KEY, token);
};

/**
 * Clear the refresh token from both cookie and localStorage.
 */
export const removeRefreshToken = (): void => {
  deleteCookie(REFRESH_TOKEN_KEY);
  safeLocalStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Generic-key variants for legacy token storage keys (e.g. `auth_token`,
 * `auth_refresh_token`). Same cookie-first read + dual-write semantics,
 * preserving the original key so existing readers keep working.
 */
export const getTokenByKey = (key: string): string | null => {
  return getCookie(key) || safeLocalStorage.getItem(key);
};

export const setTokenByKey = (key: string, token: string): void => {
  setCookie(key, token);
  safeLocalStorage.setItem(key, token);
};

export const removeTokenByKey = (key: string): void => {
  deleteCookie(key);
  safeLocalStorage.removeItem(key);
};

/**
 * Wipe every known auth-related key from cookie and localStorage.
 * Used when a 401 proves the cached token is no longer accepted by the backend
 * — we must clear ALL variants (access, refresh, legacy, tenant) so the next
 * login attempt starts from a clean slate. Missing one variant leaves the user
 * stuck in a redirect loop with a stale token.
 */
const ALL_AUTH_KEYS = [
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  'auth_token',
  'auth_refresh_token',
  'tenant_id',
  'auth_tenant_id',
  'user',
  'auth_user',
];

export const clearAllAuthStorage = (): void => {
  for (const key of ALL_AUTH_KEYS) {
    deleteCookie(key);
    safeLocalStorage.removeItem(key);
  }
};
